import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import axios from 'axios';

export default function OrderedTable(props) {
    const order = props.order;
    const { onShippingUpdate } = props; // Callback to notify parent of shipping changes
    const [totalShippingCost, setTotalShippingCost] = useState(0);
    const [isLoadingShipping, setIsLoadingShipping] = useState(true);
    const [shippingType, setShippingType] = useState(null); // 0=NoShipping, 1=AddressRequired, 2=Calculated
    const [shippingMessage, setShippingMessage] = useState('');
    const paymentType = props.paymentType;
    const roundToHundredth = (value) => {
        let placeMaker = value * 100;
        return (placeMaker / 100).toFixed(2);
    };
    
    // Fetch shipping information when zip code is entered or on initial load
    useEffect(() => {
        if (order && order.orderId) {
            setIsLoadingShipping(true);
            
            // Check if zip code is filled in (minimum requirement to attempt shipping calculation)
            const hasZipCode = order.shippingZip && order.shippingZip.length >= 5;
            
            // Check if all required address fields are filled out
            const hasCompleteAddress = order.shippingAddress && 
                                     order.shippingCity && 
                                     order.shippingState && 
                                     hasZipCode;
            
            // Build shipping request with available data
            const customerName = `${order.supporterFirstName || ''} ${order.supporterLastName || ''}`.trim();
            const shippingRequest = {
                orderId: order.orderId,
                customerName: customerName,
                shippingAddress: order.shippingAddress || '',
                shippingAddress2: order.shippingAddress2 || '',
                shippingCity: order.shippingCity || '',
                shippingState: order.shippingState || '',
                shippingZip: order.shippingZip || ''
            };
            
            // Only make API call if:
            // 1. Zip code is filled in (user is actively entering address), OR
            // 2. This is the initial load to determine shipping type (no address fields filled)
            const isInitialLoad = !order.shippingAddress && !order.shippingCity && !order.shippingState && !order.shippingZip;
            
            if (hasZipCode || isInitialLoad) {
                axios.post('api/Orders/GetShippingCost', shippingRequest)
                .then(response => {
                    const data = response.data;
                    setTotalShippingCost(data.totalShippingCost || 0);
                    setShippingType(data.shippingType); // 0=NoShipping, 1=AddressRequired, 2=Calculated
                    setShippingMessage(data.message || '');
                    setIsLoadingShipping(false);
                    
                    // Store shipping cost on order object for checkout validation
                    if (order && data.shippingType === 2) { // Calculated
                        order.clientCalculatedShippingCost = data.totalShippingCost;
                    }
                    
                    // Notify parent component of shipping changes
                    if (onShippingUpdate) {
                        onShippingUpdate({
                            shippingType: data.shippingType,
                            totalShippingCost: data.totalShippingCost || 0,
                            message: data.message || ''
                        });
                    }
                })
                .catch(error => {
                    console.error('Error fetching shipping cost:', error);
                    setTotalShippingCost(0);
                    setShippingType(0); // Default to NoShipping on error
                    setShippingMessage('');
                    setIsLoadingShipping(false);
                    
                    // Notify parent component of error state
                    if (onShippingUpdate) {
                        onShippingUpdate({
                            shippingType: 0,
                            totalShippingCost: 0,
                            message: ''
                        });
                    }
                });
            } else {
                // Address is incomplete - show address required message without API call
                setTotalShippingCost(0);
                setShippingType(1); // AddressRequired
                setShippingMessage('Please enter complete address information so shipping can be properly calculated.');
                setIsLoadingShipping(false);
                
                // Notify parent component of address required state
                if (onShippingUpdate) {
                    onShippingUpdate({
                        shippingType: 1,
                        totalShippingCost: 0,
                        message: 'Please enter complete address information so shipping can be properly calculated.'
                    });
                }
            }
        }
    }, [order?.orderId, order?.shippingAddress, order?.shippingCity, order?.shippingState, order?.shippingZip]);

    // Early return after all hooks are called
    if (!order) {
        return <div>Loading...</div>;
    }
    
    // Calculate the total with shipping and the convenience fee
    // Only include shipping in total if it's calculated (shippingType === 2)
    const shippingAmount = shippingType === 2 ? totalShippingCost : 0;
    const totalWithShipping = order ? order.orderTotal + shippingAmount : 0;
    const convenienceFee = (!paymentType || (paymentType === 2)) ? roundToHundredth(totalWithShipping * 0.05) : 0;
    const finalTotal = totalWithShipping + parseFloat(convenienceFee);


    return (<table className="table border">
        <tbody>
            <tr>
                <th>Name</th>
                <th>Quantity</th>
                <th>Price</th>
                <th>Total</th>
                <th></th>
            </tr>
            {order && order.fundraiserOrderItems.map((item) => {
                return (
                    item.fundraiserOrderItemOptions.map((itemOption, index) => {
                        if (itemOption.qty > 0)
                            return (
                                <tr key={index} className="border">
                                    <td>{itemOption.name}</td>
                                    <td>{itemOption.qty}</td>
                                    <td>${itemOption.price}</td>
                                    <td>${itemOption.price * itemOption.qty}</td>
                                    <td><Link type="button" className="border rounded p-5 px-20 bg-info text-white" to={"/Order/Edit/" + order.orderId}>Edit</Link></td>
                                </tr>
                            )
                    })
                )
            })}
            {
                order && order.isDonate &&
                        
                            <tr>
                                <td><emp>Donation</emp></td>
                                <td></td>
                                <td></td>
                        <td><strong>${order && roundToHundredth(order.orderTotal)}</strong></td>
                                <td><Link type="button" className="border rounded p-5 px-20 bg-info text-white" to={"/Order/Edit/" + order.orderId}>Edit</Link></td>
                            </tr>
                       
               
            }
            {/* Total Shipping Cost Row - Only show if shipping applies */}
            {(isLoadingShipping || shippingType !== 0) && (
                <tr>
                    <td colSpan="3">Shipping</td>
                    <td>
                        {isLoadingShipping ? (
                            <span>Loading...</span>
                        ) : shippingType === 1 ? (
                            <span style={{color: 'orange', fontStyle: 'italic'}}>
                                {shippingMessage}
                            </span>
                        ) : shippingType === 2 ? (
                            <div>
                                <div>${roundToHundredth(totalShippingCost)}</div>
                                {shippingMessage && (
                                    <div style={{color: 'orange', fontStyle: 'italic', fontSize: '0.9em', marginTop: '2px'}}>
                                        {shippingMessage}
                                    </div>
                                )}
                            </div>
                        ) : null}
                    </td>
                    <td></td>
                </tr>
            )}
            {convenienceFee > 0 &&
                <tr>
                    <td colSpan="3">Convenience Fee - 5%</td>
                    <td>${convenienceFee}</td>
                    <td></td>
                </tr>
            }
            <tr>
                <td><strong>Total</strong></td>
                <td></td>
                <td></td>
                <td><strong>${roundToHundredth(finalTotal)}</strong></td>
                <td></td>
            </tr>
        </tbody>
    </table>)
}